/**
 * Pure logic of the orders screen: which statuses a role sees and may move an
 * order to, the enrichment of the API rows, sorting, quick filters, pagination
 * and the date formatting with the order's timezone.
 */
import { PLATFORM, ROLES, type Platform, type Role } from '@/shared/constants';
import { sortByKey } from '@/shared/lib/sort';
import { SYMBOLS } from './symbols';
import {
  ABC_ORDER_TYPES,
  compareStatus,
  CS,
  DMS_ORDER_TYPES,
  ORDER_TYPES,
} from './order-types';
import type { Order, OrderPage } from './api';

export const orderTypesFor = (platform: Platform): Record<number, string> =>
  platform === PLATFORM.DMS ? DMS_ORDER_TYPES : ABC_ORDER_TYPES;

/** The status ids of the platform in display order. */
export const sortedStatusIds = (types: Record<number, string>): number[] =>
  Object.keys(types).map(Number).sort(compareStatus);

/**
 * Delivered and cancelled blocks start
 * collapsed unless a search is active; a workshop user also starts with the
 * pending block collapsed (set once in `ngOnInit`, kept until a reload).
 */
export const defaultCollapsed = (
  types: Record<number, string>,
  searchMode: boolean,
): Record<string, boolean> =>
  Object.fromEntries(
    Object.values(types).map((type) => [
      type,
      !searchMode &&
        [ORDER_TYPES[CS.DELIVERED], ORDER_TYPES[CS.CANCELLED]].includes(type),
    ]),
  );

export type EnrichedOrder = Order & {
  customizationType: string;
  expectedDeliveryDates?: Date[];
  expectedDeliveryDatesStr?: string;
  closestExpectedDeliveryDate?: Date | undefined;
  /** `moment(closest).diff(moment(), 'days') < 10`, evaluated at load time here. */
  deliverySoon?: boolean;
  itemsCount?: number;
  shirtCount?: number;
};

const pad = (n: number) => String(n).padStart(2, '0');

const partsOf = (order: Order): Record<string, unknown>[] => order.parts ?? [];

/** The customization type and, on MTM, the part counters and delivery dates. */
export const enrichOrder = (order: Order, now = Date.now()): EnrichedOrder => {
  const enriched: EnrichedOrder = {
    ...order,
    customizationType: order.displayId?.startsWith('C') ? 'Central' : '-',
  };
  if (order.platform === PLATFORM.DMS) {
    const parts = partsOf(order);
    const dates = parts
      .map((p) => p.expectedDeliveryDate)
      .filter((d): d is string => !!d)
      .map((d) => new Date(d));
    enriched.expectedDeliveryDates = dates;
    enriched.expectedDeliveryDatesStr = dates
      .map(
        (d) =>
          `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()}`,
      )
      .join('\n');
    enriched.closestExpectedDeliveryDate = dates.length
      ? new Date(Math.min(...dates.map((d) => d.getTime())))
      : undefined;
    enriched.deliverySoon =
      !!enriched.closestExpectedDeliveryDate &&
      Math.trunc(
        (enriched.closestExpectedDeliveryDate.getTime() - now) /
          (24 * 60 * 60 * 1000),
      ) < 10;
    const count = (p: Record<string, unknown>) => Number(p.count) || 1;
    enriched.itemsCount = parts.reduce((sum, p) => sum + count(p), 0);
    enriched.shirtCount = parts
      .filter(
        (p) =>
          (p.model as { data?: { productLine?: string } } | undefined)?.data
            ?.productLine === 'SHIRT',
      )
      .reduce((sum, p) => sum + count(p), 0);
  }
  return enriched;
};

export interface OrderSort {
  by: string;
  reverse: boolean;
}

export const DEFAULT_SORT: OrderSort = { by: 'createdAt', reverse: false };

/** Same column flips, a new one sorts ascending. */
export const nextOrderSort = (current: OrderSort, by: string): OrderSort =>
  current.by === by
    ? { by, reverse: !current.reverse }
    : { by, reverse: false };

const getPath = (obj: unknown, path: string): unknown =>
  path
    .split('.')
    .reduce<unknown>(
      (value, key) =>
        value && typeof value === 'object'
          ? (value as Record<string, unknown>)[key]
          : undefined,
      obj,
    );

/**
 * Lower-cased text (the display id without its
 * first character), the customization type as a second key on the workshop
 * column, reversed on demand, then the priority orders first.
 */
export const sortOrders = (
  items: readonly EnrichedOrder[],
  sort: OrderSort,
): EnrichedOrder[] => {
  const key = (item: EnrichedOrder) => {
    let value = String(getPath(item, sort.by) ?? '').toLowerCase();
    if (sort.by === 'displayId') value = value.slice(1);
    return value;
  };
  let sorted =
    sort.by === 'workshopName'
      ? sortByKey(
          sortByKey(items, (i) => i.customizationType),
          key,
        )
      : sortByKey(items, key);
  if (sort.reverse) sorted = sorted.reverse();
  return sortByKey(sorted, (item) => (item.priority === 'true' ? '0' : '1'));
};

/** The statuses a role may move an order to, per platform. */
export const nextStatusIds = (
  platform: Platform,
  role: Role,
  from: number | null | undefined,
): number[] => {
  const abc: Partial<Record<Role, Record<number, number[]>>> = {
    [ROLES.ADMIN]: {
      [CS.PENDING]: [CS.CONFIRMED, CS.CANCELLED],
      [CS.CONFIRMED]: [CS.PENDING, CS.READY_FOR_PRODUCTION, CS.CANCELLED],
      [CS.READY_FOR_PRODUCTION]: [CS.CONFIRMED, CS.IN_PROGRESS, CS.COMPLETED],
      [CS.IN_PROGRESS]: [CS.READY_FOR_PRODUCTION, CS.COMPLETED],
      [CS.COMPLETED]: [CS.READY_FOR_PRODUCTION, CS.IN_PROGRESS, CS.RECEIVED],
      [CS.RECEIVED]: [CS.COMPLETED, CS.DELIVERED],
      [CS.DELIVERED]: [CS.RECEIVED],
    },
    [ROLES.HEAD_OFFICE]: {
      [CS.CONFIRMED]: [CS.READY_FOR_PRODUCTION, CS.CANCELLED],
      [CS.READY_FOR_PRODUCTION]: [CS.CONFIRMED],
    },
    [ROLES.BOUTIQUE_MANAGER]: {
      [CS.PENDING]: [CS.CONFIRMED, CS.CANCELLED],
      [CS.CONFIRMED]: [CS.PENDING],
      [CS.COMPLETED]: [CS.RECEIVED],
      [CS.RECEIVED]: [CS.COMPLETED, CS.DELIVERED],
      [CS.DELIVERED]: [CS.RECEIVED],
    },
    [ROLES.WORKSHOP]: {
      [CS.PENDING]: [CS.CONFIRMED],
      [CS.CONFIRMED]: [CS.PENDING, CS.READY_FOR_PRODUCTION],
      [CS.READY_FOR_PRODUCTION]: [CS.IN_PROGRESS, CS.COMPLETED],
      [CS.IN_PROGRESS]: [CS.READY_FOR_PRODUCTION, CS.COMPLETED],
      [CS.COMPLETED]: [CS.READY_FOR_PRODUCTION, CS.IN_PROGRESS],
    },
  };
  const dms: Partial<Record<Role, Record<number, number[]>>> = {
    [ROLES.ADMIN]: abc[ROLES.ADMIN]!,
    [ROLES.BOUTIQUE_MANAGER]: abc[ROLES.BOUTIQUE_MANAGER]!,
  };
  const table = platform === PLATFORM.DMS ? dms : abc;
  if (from == null) return [];
  return [...(table[role]?.[from] ?? [])].sort(compareStatus);
};

/** Confirmed -> ready for production -> in progress, else +1. */
const NEXT_STATUS: Readonly<Record<number, number>> = {
  [CS.CONFIRMED]: CS.READY_FOR_PRODUCTION,
  [CS.READY_FOR_PRODUCTION]: CS.IN_PROGRESS,
};
export const incrementStatusId = (statusId: number): number =>
  NEXT_STATUS[statusId] ?? statusId + 1;

/**
 * On ABCDior, confirming a pending order of a store
 * that bypasses the head office approval sends it straight to production.
 */
export const effectiveStatusTarget = (
  order: Order,
  requested: number,
): number => {
  const bypass = (
    order.store as { bypassHeadOfficeApproval?: unknown } | null | undefined
  )?.bypassHeadOfficeApproval;
  const confirmingPending =
    order.customisationStatusId === CS.PENDING && requested === CS.CONFIRMED;
  return order.platform === PLATFORM.ABCDIOR && confirmingPending && bypass
    ? CS.READY_FOR_PRODUCTION
    : requested;
};

/** A pending order needs its Xpert transaction id. */
export const isOrderValid = (order: Order): boolean =>
  order.customisationStatusId === CS.PENDING ? !!order.externalOrderId : true;

/** Moving up (but not to cancelled) needs a valid order. */
export const updateStatusAllowed = (order: Order, target: number): boolean =>
  !(
    target !== CS.CANCELLED &&
    compareStatus(target, order.customisationStatusId) > 0 &&
    !isOrderValid(order)
  );

/** The next status is one the role may set, and no update is pending. */
export const showMarkAsButton = (
  platform: Platform,
  role: Role,
  order: Order,
  updating: boolean,
): boolean =>
  !updating &&
  nextStatusIds(platform, role, order.customisationStatusId).includes(
    incrementStatusId(order.customisationStatusId),
  );

/** Central orders in production statuses, for workshops and admins. */
export const canPrint = (role: Role, order: Order): boolean =>
  [2, 8, 3, 4].includes(order.customisationStatusId) &&
  !!order.displayId?.startsWith('C') &&
  ([ROLES.WORKSHOP, ROLES.ADMIN] as Role[]).includes(role);

export const MONTH_NAMES = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

const MONTHS = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
];

/** The moment formats used by the lists, applied to a date already shifted to its offset. */
const formatShifted = (date: Date, format: string): string => {
  const y = date.getUTCFullYear();
  const m = date.getUTCMonth();
  const d = date.getUTCDate();
  switch (format) {
    case 'MMM D, YYYY':
      return `${MONTHS[m]} ${d}, ${y}`;
    case 'MMMM D, YYYY':
      return `${MONTH_NAMES[m]} ${d}, ${y}`;
    case 'DD/MM/YYYY':
      return `${pad(d)}/${pad(m + 1)}/${y}`;
    case 'DD/MM/YYYY HH:mm':
      return `${pad(d)}/${pad(m + 1)}/${y} ${pad(date.getUTCHours())}:${pad(date.getUTCMinutes())}`;
    default:
      return date.toISOString();
  }
};

/**
 * `moment(value).utcOffset(-timezoneOffset)`, the
 * offset being the browser-style one stored on the order (Paris, -120, by
 * default); '-' when the field is missing.
 */
export const formatOrderTime = (
  order: Order,
  format: string,
  field: 'createdAt' | 'receivedDateBlois' | 'shipmentDateBlois' = 'createdAt',
): string => {
  const value = order[field];
  if (!value) return '-';
  const offsetMinutes = -(order.timezoneOffset ?? -120);
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'Invalid date';
  return formatShifted(
    new Date(date.getTime() + offsetMinutes * 60_000),
    format,
  );
};

/** `moment(date).format('DD/MM/YYYY')` in the browser timezone. */
export const formatLocalDate = (date: Date): string =>
  `${pad(date.getDate())}/${pad(date.getMonth() + 1)}/${date.getFullYear()}`;

/** `moment(date).tz('Europe/Paris').format('DD/MM/YYYY HH:mm')`. */
export const formatParisTime = (value: string | null | undefined): string => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'Invalid date';
  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone: 'Europe/Paris',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).formatToParts(date);
  const get = (type: string) => parts.find((p) => p.type === type)?.value ?? '';
  return `${get('day')}/${get('month')}/${get('year')} ${get('hour')}:${get('minute')}`;
};

/** `-120` -> `+0200`. */
export const timezoneLabel = (offset: number): string => {
  const sign = offset < 0 ? '+' : '-';
  const abs = Math.abs(offset);
  return `${sign}${pad(Math.trunc(abs / 60))}${pad(abs % 60)}`;
};

/** The page numbers shown between the ellipses. */
export const midPages = (pagination: {
  totalPages?: number | null | undefined;
  page?: number | null | undefined;
}): number[] => {
  const totalPages = pagination.totalPages ?? 0;
  const page = pagination.page ?? 1;
  if (totalPages >= 8) {
    if (page <= 4) return [1, 2, 3, 4, 5, 6];
    if (totalPages - page <= 3)
      return [
        totalPages - 5,
        totalPages - 4,
        totalPages - 3,
        totalPages - 2,
        totalPages - 1,
        totalPages,
      ];
    return [page - 2, page - 1, page, page + 1, page + 2];
  }
  return Array.from({ length: totalPages }, (_, i) => i + 1);
};

/** A loaded page plus the flag set while another page of the block is requested. */
export type OrderPagination = OrderPage & { changingPage?: boolean };

export const DEFAULT_PAGINATION: OrderPagination = {
  docs: [],
  page: 1,
  totalPages: 0,
};

/** The custom text split around `<symbol>` tags rendered in the Dior font. */
export const textParts = (
  text: string | null | undefined,
): { value: string; symbol?: boolean }[] => {
  const source = text ?? '';
  const symbols = source.match(/<[a-z]+>/g);
  if (!symbols) return [{ value: source }];
  const pieces = source.split(/<[a-z]+>/g);
  return pieces.flatMap((piece, i) => [
    ...(piece !== '' ? [{ value: piece }] : []),
    ...(i < pieces.length - 1
      ? [
          {
            value: SYMBOLS[symbols[i]!.replace(/^<|>$/g, '')] ?? '',
            symbol: true,
          },
        ]
      : []),
  ]);
};

/** The validation filters of the MTM production blocks. */
export const quickFilters = (
  platform: Platform,
  statusId: number,
): string[] => {
  if (platform !== PLATFORM.DMS) return [];
  switch (statusId) {
    case CS.CONFIRMED:
      return ['all', 'no_validation', 'sku'];
    case CS.READY_FOR_PRODUCTION:
      return ['all', 'no_validation', 'oa', 'label_barcode'];
    case CS.IN_PROGRESS:
      return ['all', 'no_validation', 'price_generation', 'purchase_order'];
    default:
      return [];
  }
};

const partsWoCancelled = (order: Order) =>
  partsOf(order).filter((p) => p.customisationStatusId !== CS.CANCELLED);

/**
 * The quick filters that only look at the parts
 * and the product flags. The `label_barcode` and ready-for-production
 * `no_validation` filters need the measure types of the parts: they come with
 * the MTM details (see migration.md).
 */
export const applyQuickFilter = (
  platform: Platform,
  statusId: number,
  items: readonly EnrichedOrder[],
  filter: string,
): EnrichedOrder[] => {
  if (platform !== PLATFORM.DMS) return [...items];
  const product = (o: Order) => (o.product ?? {}) as Record<string, unknown>;
  switch (filter) {
    case 'no_validation':
      if (statusId === CS.CONFIRMED)
        return items.filter((o) => partsWoCancelled(o).some((p) => !p.sku));
      if (statusId === CS.IN_PROGRESS)
        return items.filter(
          (o) => !product(o).dmsPriceGeneration && !product(o).dmsPurchaseOrder,
        );
      return [...items];
    case 'sku':
      return items.filter((o) => partsWoCancelled(o).every((p) => p.sku));
    case 'oa':
      return items.filter((o) => partsWoCancelled(o).every((p) => p.oaCode));
    case 'price_generation':
      return items.filter((o) => !!product(o).dmsPriceGeneration);
    case 'purchase_order':
      return items.filter((o) => !!product(o).dmsPurchaseOrder);
    default:
      return [...items];
  }
};

/** One Excel row per order. */
export const REPORT_COLUMNS = [
  'Personalization ID',
  'Seller',
  'Product Line',
  'Commercial Reference',
  'M3SKU Stock',
  'Product Name',
  'Color',
  'Size',
  'OA Reference',
  'Text',
  'Alphabet',
  'Xpert Transaction ID',
  'beDiorID',
  'Region',
  'Country',
  'Store Code',
  'Store Name',
  'Status',
  'Workshop',
  'Workshop Type',
  'Creation Date (Paris time)',
  'Timezone',
  'Creation Date (Local time)',
  'Just Purchased',
  'Additional Notes',
] as const;

const WORKSHOP_TYPE_LABELS: Readonly<Record<string, string>> = {
  sav: 'Care',
  central: 'Central',
  inStore: 'Permanent In-store',
};

/** The size record of the product: the chosen one, or the only one. */
const productSize = (product: NonNullable<Order['product']>) => {
  const sizes = product.sizes ?? [];
  if (product.size)
    return sizes.find((s) => String(s.size) === String(product.size)) ?? null;
  return product.hasUniqueSize ? (sizes[0] ?? null) : null;
};

export const reportRow = (
  cus: Order,
  role: Role,
): Record<(typeof REPORT_COLUMNS)[number], unknown> => {
  const product = cus.product ?? {};
  const size = productSize(product);
  const timezoneOffset = cus.timezoneOffset ?? 0;
  const hasTimezone = timezoneOffset !== 0;
  return {
    'Personalization ID': cus.displayId,
    Seller: cus.sellerName || '',
    'Product Line': product.line,
    'Commercial Reference': cus.productReference,
    'M3SKU Stock': size ? size.m3SkuStock : '',
    'Product Name': cus.productName,
    Color: cus.color?.name || cus.productColor,
    Size: product.size || size?.size,
    'OA Reference': cus.oaCode,
    Text: cus.customText,
    Alphabet: cus.alphabet || 'latin',
    'Xpert Transaction ID': cus.externalOrderId,
    beDiorID: role === ROLES.WORKSHOP ? 'XXX' : cus.externalClientId,
    Region: cus.storeRegion,
    Country: cus.storeCountry,
    'Store Code': cus.storeCode,
    'Store Name': cus.storeName,
    Status: ORDER_TYPES[cus.customisationStatusId],
    Workshop: cus.workshop?.name ?? '',
    'Workshop Type': WORKSHOP_TYPE_LABELS[cus.workshopType ?? ''] ?? 'Event',
    'Creation Date (Paris time)': formatParisTime(cus.createdAt),
    Timezone: hasTimezone ? timezoneLabel(timezoneOffset) : '',
    'Creation Date (Local time)': hasTimezone
      ? formatOrderTime(cus, 'DD/MM/YYYY HH:mm')
      : '',
    'Just Purchased': cus.purchaseStatus,
    'Additional Notes': cus.notes,
  };
};

/** `abcdior_DDMMYYYY_HHmmss.xlsx`. */
export const reportFileName = (now: Date): string =>
  `abcdior_${pad(now.getDate())}${pad(now.getMonth() + 1)}${now.getFullYear()}_${pad(now.getHours())}${pad(now.getMinutes())}${pad(now.getSeconds())}.xlsx`;
